"use client";

import { formatDateString } from "@/apiUtils/commonUtils";
import { dateFormats, eyeTestServiceType } from "@/apiUtils/constants";
import { cancelAppointment } from "@/apiUtils/storeUtils";
import IconContactLensV2 from "@/common/core/icons/IconContactLensV2";
import IconDrivingLicense from "@/common/core/icons/IconDrivingLicense";
import IconEye from "@/common/core/icons/IconEye";
import {
  BookingDetails,
  StoreType,
  SuccessSectionContent,
  TestTypeContent,
  TypeToastMessage,
  CalendarEventDetails,
} from "@/common/lib/types";
import { useGlobalStore } from "@/store/store";
import { useRouter } from "next/navigation";
import { FC, useState, useMemo, ReactNode, useImperativeHandle, forwardRef } from "react";
import EyeTestSuccessView from "./view";
import Image from "next/image";
import { getTranslatedServiceName, SupportedLocale } from "@/api/mReserve/serviceTranslations";

interface EyeTestSuccessProps {
  bookingDetails?: BookingDetails;
  storeDetails?: StoreType;
  content?: SuccessSectionContent;
  locale?: string;
}

export interface EyeTestSuccessRef {
  openCancelModal: () => void;
}

const EyeTestSuccess = forwardRef<EyeTestSuccessRef, EyeTestSuccessProps>(({
  bookingDetails,
  storeDetails,
  content,
  locale,
}, ref) => {
  const { date, startTime, endTime } = bookingDetails || {};
  const [bookingToast, setBookingToast] = useState<TypeToastMessage>({ open: false });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const slotDate = date ? formatDateString(date, dateFormats.DD_MMMM_YYYY) : "";
  const { user } = useGlobalStore();
  const router = useRouter();

  useImperativeHandle(ref, () => ({
    openCancelModal: () => setIsModalOpen(true),
  }));

  const handleCancelBooking = async (reason: string) => {
    if (bookingDetails?.id && storeDetails?.id) {
      const response = await cancelAppointment(bookingDetails?.id, storeDetails?.id, locale, reason);

      if (response?.data?.success) {
        setBookingToast({
          open: true,
          message: content?.cancelConfirmationToastMessage,
          type: "success",
        });
        if (user) {
          router.push("/my-account/eye-test-reservations");
        } else {
          router.push("/");
        }
      } else if (response?.error?.message) {
        setBookingToast({ open: true, message: response?.error?.message, type: "danger" });
      } else {
        setBookingToast({
          open: true,
          message: "Something went wrong. Please try again",
          type: "danger",
        });
      }
      setIsModalOpen(false);
      setTimeout(() => setBookingToast({ open: false }), 2500);
    }
  };

  const { title, icon } = useMemo(() => {
    

    // Prefer serviceIcon from API if available, otherwise fallback to hardcoded icons
    const serviceIcon = (bookingDetails as any)?.serviceIcon as string | undefined;

    let resolvedIcon: ReactNode;
    if (serviceIcon) {
      resolvedIcon = (
        <Image
          src={serviceIcon}
          alt={bookingDetails?.eyeTestType || "Service"}
          width={24}
          height={24}
          className="icon md me-4"
          style={{ objectFit: "contain" }}
          unoptimized
        />
      );
    }

    const title = getTranslatedServiceName(bookingDetails?.serviceTitle ?? "", locale?.split("-")[1] as SupportedLocale);
    return { title, icon: resolvedIcon };
  }, [bookingDetails, locale]);

  const eventDetails: CalendarEventDetails = useMemo(
    () => ({
      date,
      startTime,
      endTime,
      title,
      location: storeDetails?.name,
    }),
    [date, endTime, startTime, storeDetails?.name, title],
  );

  const closeBookingToast = () => {
    setBookingToast({ open: false, message: "" });
  };

  return (
    <EyeTestSuccessView
      bookingId={bookingDetails?.id}
      locale={locale}
      storeDetails={storeDetails}
      slotDate={slotDate}
      startTime={startTime}
      endTime={endTime}
      content={content}
      serviceTitle={title}
      icon={icon}
      eventDetails={eventDetails}
      bookingToast={bookingToast}
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      handleCancelBooking={handleCancelBooking}
      closeBookingToast={closeBookingToast}
    />
  );
});

export default EyeTestSuccess;
